<template>
	<component
		custom
		:to="navigateTo"
		:disabled="!navigateOnClick"
		class="router-link width-100"
		:event="navigateOnClick ? 'click' : ''"
		:is="navigateOnClick ? 'router-link' : 'div'"
	>
		<div class="card-container width-100 display-flex flex-row flex-align-center flex-justify-space-between pr-2">
			<div class="display-flex flex-row flex-align-center">
				<div class="workflow-form-icon-container ml-1 mt-1" @click="(e) => e.preventDefault()">
					<ButtonsDropdownIcon class="icon-container" :buttonDropdown="buttonDropdown" />
				</div>
				<ButtonsDropdownIconsCard class="ml-1 mr-3" :buttonDropdown="buttonDropdown" />
				<div class="display-flex flex-column flex-justify-center">
					<AppLoader v-if="isSavingTitle" />
					<template v-else>
						<EditableTextField
							ref="text_field"
							placeholder="Enter Name"
							v-if="isEditingTitle"
							:text="buttonDropdownTitle"
							@click="(e) => e.preventDefault()"
							:isEditing.sync="isEditingTitle"
							@onSave="(v) => (buttonDropdownTitle = v)"
						/>
						<div v-else class="buttons-dropdown-title">{{ buttonDropdownTitle }}</div>
					</template>
					<Row class="workflow-description flex-align-center">
						<ProjectChip :projectId="buttonDropdown.project" />
					</Row>
				</div>
			</div>
			<Row class="flex-align-center">
				<AppDropdownMenu
					left
					bottom
					absolute
					min-width="200"
					content-class="men"
					:items="enabledMenuItems"
					v-if="enabledMenuItems && enabledMenuItems.length !== 0"
				>
					<template #activator>
						<AnemicButton
							@click="(e) => e.preventDefault()"
							width="22"
							height="22"
							icon
							fab
							x-small
							class="flex-centerize menu-button mr-2"
						>
							<AppIcon name="three-dot-menu" width="13" class="menu-icon" />
						</AnemicButton>
					</template>
					<template #list-item="{ item }">
						<h3 v-if="!item.divider" class="reg">{{ item.text }}</h3>
					</template>
				</AppDropdownMenu>
				<!-- v-else here cause we need to put a transparent placeholder -->
				<AnemicButton
					fab
					icon
					v-else
					x-small
					rounded
					disabled
					width="22"
					height="22"
					class="flex-centerize mr-2"
					style="background: transparent !important"
				/>
			</Row>
		</div>
	</component>
</template>
<script>
import ProjectChip from '@/views/Flow/ProjectChip.vue';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import EditableTextField from '@/components/common/EditableTextField.vue';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { EDIT_BUTTON_DROPDOWN_ROUTE_NAME } from '@/router/router.const';
import { updateButtonDropdown } from '@/store/modules/buttonDropdowns/actions';
import { deleteButtonDropdown } from '@/store/modules/buttonDropdowns/actions.js';
import ButtonsDropdownIcon from '@/components/ButtonsDropdown/ButtonsDropdownIcon.vue';
import ButtonsDropdownIconsCard from '@/components/Modules/ButtonsDropdownIconsCard.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const';
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';
export default {
	name: 'ButtonsDropdownCard',
	components: {
		ProjectChip,
		EditableTextField,
		ButtonsDropdownIcon,
		ButtonsDropdownIconsCard,
	},
	props: {
		buttonDropdown: { type: Object, required: true },
		navigateOnClick: { type: Boolean, default: true },
	},
	computed: {
		...mapGettersShorthand({ projectHasPermission }),
		navigateTo() {
			return { name: EDIT_BUTTON_DROPDOWN_ROUTE_NAME, params: { id: this.buttonDropdown.id } };
		},
		hasEditPermissions() {
			return this.projectHasPermission(this.buttonDropdown.project, ProjectPermissions.ManageButtonDropdowns);
		},
		hasDeletePermission() {
			return this.projectHasPermission(this.buttonDropdown.project, ProjectPermissions.ManageButtonDropdowns);
		},
		buttonDropdownTitle: {
			get() {
				return this.buttonDropdown.title;
			},
			async set(value) {
				this.isSavingTitle = true;
				try {
					await this.updateButtonDropdown({ id: this.buttonDropdown.id, data: { title: value } });
				} catch {
					this.$toast.error('Failed to update title, please try again later');
				} finally {
					this.isSavingTitle = false;
				}
			},
		},
		menuItems() {
			const items = [];
			if (this.hasEditPermissions) {
				items.push({ text: 'Rename', onClick: this.renameButtonDropdown });
			}
			if (this.hasDeletePermission) {
				if (items.length > 0) {
					items.push({ divider: true });
				}
				items.push({ text: 'Delete', onClick: this.openDeleteDialog });
			}
			return items;
		},

		enabledMenuItems() {
			return this.menuItems.filter((x) => !x.disabled);
		},
	},
	methods: {
		...mapActionsShorthand({ deleteButtonDropdown, updateButtonDropdown }),
		renameButtonDropdown() {
			this.isEditingTitle = true;
			setTimeout(() => {
				this.$refs.text_field.focus();
			}, 100);
		},
		async openDeleteDialog() {
			const dialogProps = {
				noText: 'Cancel',
				yesText: 'Delete',
				description: 'It might take a minute to complete this action',
				title: 'Are you sure you want to delete this Button dropdown?',
			};
			const res = await AppDialogManagerService.openConfirmationDialog(dialogProps);
			if (res === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				await this.deleteButtonDropdown({ id: this.buttonDropdown.id });
			}
		},
	},
	data() {
		return {
			isSavingTitle: false,
			isEditingTitle: false,
		};
	},
};
</script>

<style scoped lang="scss">
.router-link {
	text-decoration: none !important;
}
.card-container {
	height: 70px;
	border-radius: 13px;
	background-color: var(--bg-color);

	&:hover {
		cursor: pointer;
		transition: 0.1s;
		background: #f5f7fa;

		.menu-button {
			border: 1px solid var(--forth-border-color);
		}
	}

	&:not(:hover) {
		transition: 0.1s;
		::v-deep {
			.buttons-dropdown-icon {
				transition: 0.1s;
				color: #90909070 !important;
			}
		}
	}
}

.workflow-form-icon-container {
	width: 34px;
}

.buttons-dropdown-title {
	font-size: 15px;
	font-weight: normal;
	color: var(--primary-font-color);

	overflow: hidden;
	display: -webkit-box;
	text-overflow: ellipsis;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2; // num of rows to break
}

.menu-button {
	background: transparent !important;
}
.icon-container {
	transform: scale(0.8);
}
</style>
